import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS, GCS_BUCKET_URL } from '../../config/api';
import { apiPost } from '../../utils/apiUtils';
import { checkConditionals } from '../../utils/helper';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import { loadTestState, saveTestState, clearTestState, mergeSavedFlags, mergeSavedScales } from '../../utils/testStateStorage';import './InteractiveSceneTestCanvas.css';

const InteractiveSceneTestCanvas = ({
  novelId,
  sceneId,
  scene,
  novel,
  loading,
  setLoading,
  error,
  setError,
  isProduction = false,
  sceneType = 'interactive'
}) => {
  const navigate = useNavigate();
  const allEndings = getAllEndingsFromNovel(novel);
  const [endingsSearch, setEndingsSearch] = useState('');
  // Test Game Canvas state
  const [testGameState, setTestGameState] = useState({
    flags: [],
    scales: [],
    completedEndingIds: [],
    userMessage: '',
    characterMessage: '',
    characterName: '',
    messageHistory: [],
    userCharacterName: '',
    userCharacterBiography: '',
    // Add direct asset references for display
    currentSprites: [],
    currentBackground: null,
    currentMusic: null,
    // Track if we should evaluate conditionals or use direct assets
    useDirectAssets: false,
    reachedEndingName: null,
    endingReasoning: null,
    contextEndingReached: false,
    nextScene: null,
    // Open world specific
    generatedCharacters: [],
    generatedBackgrounds: [],
    currentCharsInScene: [],
    currentBackgroundId: null,
    deadCustomCharIds: []
  });

  // Local loading state for test canvas
  const [testLoading, setTestLoading] = useState(false);

  // Dynamic sprite positioning
  const [spritesTop, setSpritesTop] = useState(0);
  const messageContainerRef = useRef(null);
  const msgUiRef = useRef(null);
  const historyBodyRef = useRef(null);

  const [testControlsExpanded, setTestControlsExpanded] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(true);
  const [audioRef, setAudioRef] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  // Snapshot of flags/scales/completed endings captured before the first message of a
  // conversation, used by the "rollback to conversation start" button. Null until first message.
  const [convStartSnapshot, setConvStartSnapshot] = useState(null);

  // Calculate sprite top position based on message UI height
  useEffect(() => {
    const calcTop = () => {
      if (msgUiRef.current) {
        setSpritesTop(msgUiRef.current.offsetTop + msgUiRef.current.offsetHeight + 4);
      }
    };
    calcTop();
    const observer = new ResizeObserver(calcTop);
    if (msgUiRef.current) observer.observe(msgUiRef.current);
    window.addEventListener('resize', calcTop);
    return () => { observer.disconnect(); window.removeEventListener('resize', calcTop); };
  }, [testGameState.characterMessage]);

  // Build the initial scene snapshot (start message + open world starting chars/bg)
  const buildStartScene = (userName, flagsForDisplay, scalesForDisplay) => {
    let initialCharsInScene = [];
    let initialBackgroundId = null;
    if (sceneType === 'open_world') {
      const startingBg = (scene?.backgrounds_custom || []).find(bg => bg.starts);
      if (startingBg) initialBackgroundId = String(startingBg.background_id);
      for (const cc of (scene?.character_custom || [])) {
        if (cc.starts && cc.start_sprite_id) {
          initialCharsInScene.push({ char_id: String(cc.character_id), sprite_id: String(cc.start_sprite_id) });
        }
      }
    }

    let initialMessageHistory = [];
    if (scene?.start_message_original) {
      const speaker = novel?.characters?.find(c => c.id === scene.start_message_speaker_id);
      const speakerName = speaker ? speaker.prompt_name : 'Рассказчик';
      initialMessageHistory.push({
        id: scene.start_message_speaker_id || 'NARRATOR',
        name: speakerName,
        message: scene.start_message_original.replace(/{{user}}/g, userName || 'Игрок'),
        location_id: initialBackgroundId,
        characters_in_scene: initialCharsInScene,
        displayInfo: {
          sprites: getActiveCharacters(),
          background: getActiveBackground(),
          music: getActiveMusic(),
          flags: [...flagsForDisplay],
          scales: [...scalesForDisplay]
        }
      });
    }
    return { initialCharsInScene, initialBackgroundId, initialMessageHistory };
  };

  // Initialize test game state from novel data, restoring any saved
  // per-novel playthrough state (flags/scales/completed endings/name/bio) from localStorage
  const initializeTestGameState = () => {
    const saved = loadTestState(novelId);
    const initialFlags = mergeSavedFlags(novel?.states?.flags, saved?.flags);
    const initialScales = mergeSavedScales(novel?.states?.scales, saved?.scales);
    const initialCompletedEndingIds = saved?.completedEndingIds || [];
    const userName = saved?.userCharacterName || testGameState.userCharacterName || 'Игрок';
    const { initialCharsInScene, initialBackgroundId, initialMessageHistory } = buildStartScene(userName, initialFlags, initialScales);

    setTestGameState({
      flags: initialFlags,
      scales: initialScales,
      completedEndingIds: initialCompletedEndingIds,
      userMessage: '',
      characterMessage: initialMessageHistory.length > 0 ? initialMessageHistory[0].message : '',
      characterName: initialMessageHistory.length > 0 ? initialMessageHistory[0].name : '',
      messageHistory: initialMessageHistory,
      userCharacterName: saved?.userCharacterName || '',
      userCharacterBiography: saved?.userCharacterBiography || '',
      reachedEndingName: null,
      endingReasoning: null,
      contextEndingReached: false,
      nextScene: null,
      generatedCharacters: [],
      generatedBackgrounds: [],
      currentCharsInScene: initialCharsInScene,
      currentBackgroundId: initialBackgroundId,
      deadCustomCharIds: []
    });
  };

  // Clear the conversation history AND roll flags/scales/completed endings back to the
  // snapshot captured before the first message of this conversation (if any), so endings /
  // continuous changes accumulated during the run are undone. Keeps name/bio. With no
  // snapshot (no message sent yet) the current states are already the start, so they're kept.
  const handleClearHistory = () => {
    const restoredFlags = convStartSnapshot ? convStartSnapshot.flags.map(f => ({ ...f })) : testGameState.flags;
    const restoredScales = convStartSnapshot ? convStartSnapshot.scales.map(s => ({ ...s })) : testGameState.scales;
    const restoredEndings = convStartSnapshot ? [...convStartSnapshot.completedEndingIds] : testGameState.completedEndingIds;
    const { initialCharsInScene, initialBackgroundId, initialMessageHistory } =
      buildStartScene(testGameState.userCharacterName, restoredFlags, restoredScales);
    setTestGameState(prev => ({
      ...prev,
      flags: restoredFlags,
      scales: restoredScales,
      completedEndingIds: restoredEndings,
      messageHistory: initialMessageHistory,
      characterMessage: initialMessageHistory.length > 0 ? initialMessageHistory[0].message : '',
      characterName: initialMessageHistory.length > 0 ? initialMessageHistory[0].name : '',
      userMessage: '',
      useDirectAssets: false,
      reachedEndingName: null,
      endingReasoning: null,
      contextEndingReached: false,
      nextScene: null,
      generatedCharacters: [],
      generatedBackgrounds: [],
      currentCharsInScene: initialCharsInScene,
      currentBackgroundId: initialBackgroundId,
      deadCustomCharIds: []
    }));
    setConvStartSnapshot(null);
    setCurrentHistoryIndex(initialMessageHistory.length - 1);
    persistState({ flags: restoredFlags, scales: restoredScales, completedEndingIds: restoredEndings });
  };

  // Reset flags/scales/completed endings to the novel's initial values (keep history/name/bio).
  // The save effect re-persists the cleared state alongside the preserved name/bio.
  const handleClearState = () => {
    const initFlags = (novel?.states?.flags || []).map(f => ({ id: f.id, name: f.name, value: f.initial_value }));
    const initScales = (novel?.states?.scales || []).map(s => ({ id: s.id, name: s.name, value: s.initial_value }));
    setTestGameState(prev => ({
      ...prev,
      flags: initFlags,
      scales: initScales,
      completedEndingIds: [],
      reachedEndingName: null,
      endingReasoning: null,
      contextEndingReached: false,
      nextScene: null
    }));
    setConvStartSnapshot(null);
  };

  // Initialize test state when component mounts or novel changes
  useEffect(() => {
    if (novel && scene) {
      initializeTestGameState();
      // Always evaluate conditionals on initial load
      setTestGameState(prev => ({
        ...prev,
        useDirectAssets: false
      }));
    }
  }, [novel, scene]);

  // Persist flags/scales/completed endings/name/bio per novel so progress carries across scenes.
  // Saved explicitly on each mutation (turns, endings, manual edits) — NOT via a state-reading
  // effect, which would write the empty initial state before the novel loads and clobber storage.
  const persistState = (overrides = {}) => {
    if (!novelId) return;
    saveTestState(novelId, {
      flags: overrides.flags ?? testGameState.flags,
      scales: overrides.scales ?? testGameState.scales,
      completedEndingIds: overrides.completedEndingIds ?? testGameState.completedEndingIds,
      userCharacterName: overrides.userCharacterName ?? testGameState.userCharacterName,
      userCharacterBiography: overrides.userCharacterBiography ?? testGameState.userCharacterBiography
    });
  };

  // Get active background based on conditionals or direct assets
  const getActiveBackground = () => {
    // Open world: handle generated and custom backgrounds
    if (sceneType === 'open_world') {
      if (testGameState.useDirectAssets && testGameState.currentBackgroundId) {
        // Check if it's a generated background
        const genBg = testGameState.generatedBackgrounds.find(g => g.back_id === testGameState.currentBackgroundId);
        if (genBg) {
          return { id: genBg.back_id, is_generated: true, image_base64: genBg.image_base64 };
        }
        // Custom background from novel assets
        if (testGameState.currentBackground) return testGameState.currentBackground;
        if (novel?.assets?.backgrounds) {
          return novel.assets.backgrounds.find(b => b.id === testGameState.currentBackgroundId) || null;
        }
        return null;
      }
      // Initial state: find the background with starts=true
      if (!scene?.backgrounds_custom || !novel?.assets?.backgrounds) return null;
      const startingBg = scene.backgrounds_custom.find(bg => bg.starts);
      if (!startingBg) return null;
      return novel.assets.backgrounds.find(b => b.id === startingBg.background_id) || null;
    }

    // If we should use direct assets from API response, return the current background
    if (testGameState.useDirectAssets && testGameState.currentBackground) {
      return testGameState.currentBackground;
    }

    // Otherwise evaluate conditionals (initial load or after rollback)
    if (!scene || !scene.backgrounds || !novel || !novel.assets || !novel.assets.backgrounds) {
      return null;
    }

    // Convert flags and scales to format needed for checkConditionals
    const currentFlags = testGameState.flags.map(flag => ({
      id: flag.id,
      value: flag.value
    }));

    const currentScales = testGameState.scales.map(scale => ({
      id: scale.id,
      value: scale.value
    }));

    // Find background that meets conditionals with highest priority
    let highestPriorityBackground = null;
    let highestPriority = -1;

    for (const background of scene.backgrounds) {
      const meetsConditionals = checkConditionals(
        background.conditionals || [],
        currentFlags,
        currentScales,
        testGameState.completedEndingIds || []
      );

      if (meetsConditionals && background.priority > highestPriority) {
        // Try both background_id and background_asset_id fields
        const backgroundId = background.background_id || background.background_asset_id;
        const backgroundAsset = novel.assets.backgrounds.find(b => b.id === backgroundId);
        if (backgroundAsset) {
          highestPriorityBackground = backgroundAsset;
          highestPriority = background.priority;
        }
      }
    }

    return highestPriorityBackground;
  };

  // Get active characters based on conditionals or direct assets
  const getActiveCharacters = () => {
    // Open world: use currentCharsInScene after interaction, or starts=true initially
    if (sceneType === 'open_world') {
      if (testGameState.useDirectAssets && testGameState.currentCharsInScene.length > 0) {
        // After interaction: build from currentCharsInScene
        const activeCharacters = [];
        for (const cs of testGameState.currentCharsInScene) {
          // Check if it's a generated character
          const genChar = testGameState.generatedCharacters.find(g => g.char_id === cs.char_id);
          if (genChar) {
            activeCharacters.push({
              id: cs.char_id,
              character_id: cs.char_id,
              is_generated: true,
              sprite_base64: genChar.sprite_base64,
              name: genChar.char_name,
            });
          } else {
            // Custom character from novel assets
            const spriteId = cs.sprite_id;
            if (spriteId && novel?.assets?.sprites) {
              const spriteAsset = novel.assets.sprites.find(s => s.id === spriteId);
              if (spriteAsset) {
                activeCharacters.push({
                  ...spriteAsset,
                  character_id: cs.char_id,
                  is_generated: false,
                });
              }
            }
          }
        }
        return activeCharacters;
      }

      // Initial state: find characters with starts=true
      if (!scene?.character_custom || !novel?.assets?.sprites) return [];
      const activeCharacters = [];
      for (const character of scene.character_custom) {
        if (character.starts && character.start_sprite_id) {
          const characterSprite = novel.assets.sprites.find(s => s.id === character.start_sprite_id);
          if (characterSprite) {
            activeCharacters.push({
              ...characterSprite,
              character_id: character.character_id,
              is_generated: false,
            });
          }
        }
      }
      return activeCharacters;
    }

    // If we should use direct assets from API response, return the current sprites
    if (testGameState.useDirectAssets && testGameState.currentSprites && testGameState.currentSprites.length > 0) {
      return testGameState.currentSprites;
    }

    // Otherwise evaluate conditionals (initial load or after rollback)
    if (!scene || !scene.characters || !novel || !novel.assets || !novel.assets.sprites) {
      return [];
    }

    // Convert flags and scales to format needed for checkConditionals
    const currentFlags = testGameState.flags.map(flag => ({
      id: flag.id,
      value: flag.value
    }));

    const currentScales = testGameState.scales.map(scale => ({
      id: scale.id,
      value: scale.value
    }));

    // Find characters that meet conditionals and get their sprites.
    // A character can be added several times with different conditionals — the first passing entry wins (same as backend).
    const activeCharacters = [];
    const seenCharacterIds = new Set();

    for (const character of scene.characters) {
      if (seenCharacterIds.has(String(character.character_id))) continue;
      const meetsConditionals = checkConditionals(
        character.conditionals || [],
        currentFlags,
        currentScales,
        testGameState.completedEndingIds || []
      );

      if (meetsConditionals) seenCharacterIds.add(String(character.character_id));
      if (meetsConditionals && character.start_sprite_id) {
        const characterSprite = novel.assets.sprites.find(s => s.id === character.start_sprite_id);
        if (characterSprite) {
          activeCharacters.push({
            ...characterSprite,
            character_id: character.character_id,
            position: character.position || 'center'
          });
        }
      }
    }

    return activeCharacters;
  };

  // Get active music based on scene music or direct assets
  const getActiveMusic = () => {
    // If we should use direct assets from API response, return the first track from the music array
    if (testGameState.useDirectAssets && testGameState.currentMusic) {
      // currentMusic could be an array (from test API) or a single object (from rollback)
      if (Array.isArray(testGameState.currentMusic) && testGameState.currentMusic.length > 0) {
        return testGameState.currentMusic[0]; // Play first track from playlist
      } else if (testGameState.currentMusic && !Array.isArray(testGameState.currentMusic)) {
        return testGameState.currentMusic; // Single track
      }
      return null;
    }

    // Otherwise evaluate conditionals (initial load or after rollback) - use start_asset_music_id
    if (!scene || !scene.music || !scene.music.start_asset_music_id || !novel || !novel.assets || !novel.assets.musics) {
      return null;
    }

    return novel.assets.musics.find(m => m.id === scene.music.start_asset_music_id) || null;
  };

  // Get available static endings based on conditionals
  const getAvailableStaticEndings = () => {
    if (!scene || !scene.static_endings || scene.static_endings.length === 0) {
      return [];
    }

    // Convert flags and scales to format needed for checkConditionals
    const currentFlags = testGameState.flags.map(flag => ({
      id: flag.id,
      value: flag.value
    }));

    const currentScales = testGameState.scales.map(scale => ({
      id: scale.id,
      value: scale.value
    }));

    // Filter static endings based on conditionals
    return scene.static_endings.filter(ending =>
      checkConditionals(
        ending.conditionals || [],
        currentFlags,
        currentScales,
        testGameState.completedEndingIds || []
      )
    );
  };

  // Update flag value
  const updateFlagValue = (flagId, value) => {
    const next = testGameState.flags.map(flag => flag.id === flagId ? { ...flag, value } : flag);
    setTestGameState(prev => ({ ...prev, flags: next }));
    persistState({ flags: next });
  };

  // Update scale value
  const updateScaleValue = (scaleId, value) => {
    const parsed = parseInt(value);
    const next = testGameState.scales.map(scale => scale.id === scaleId ? { ...scale, value: Number.isNaN(parsed) ? 0 : parsed } : scale);
    setTestGameState(prev => ({ ...prev, scales: next }));
    persistState({ scales: next });
  };

  // Handle sending test message
  const handleSendTestMessage = async () => {
    if (!testGameState.userMessage.trim() || testLoading || scene?.interactive === false) return;

    // On the first message of the conversation, snapshot current states for easy rollback
    if (!convStartSnapshot) {
      setConvStartSnapshot({
        flags: testGameState.flags.map(f => ({ ...f })),
        scales: testGameState.scales.map(s => ({ ...s })),
        completedEndingIds: [...testGameState.completedEndingIds]
      });
    }

    setTestLoading(true);
    setError('');

    try {
      // Don't add user message here - backend will handle it
      // This prevents duplicate messages in dialogue history
      const newMessageHistory = testGameState.messageHistory;

      const currentSprites = getActiveCharacters();

      let response;

      if (sceneType === 'open_world') {
        // Build open world request
        const openWorldRequest = {
          user_message: testGameState.userMessage,
          user_biography_name: testGameState.userCharacterName || 'Incognito',
          user_biography_description: testGameState.userCharacterBiography || 'A user testing the interactive scene.',

          music_mixes: novel?.music_mixes || [],

          current_flags: testGameState.flags.map(flag => ({
            id: flag.id, name: flag.name, initial_value: flag.value
          })),
          current_scales: testGameState.scales.map(scale => ({
            id: scale.id, name: scale.name, initial_value: Number.isFinite(scale.value) ? scale.value : 0
          })),
          completed_endings_ids: testGameState.completedEndingIds,

          world_prompts: novel?.world_prompts || [],
          characters: novel?.characters || [],
          assets: novel?.assets || { sprites: [], backgrounds: [], musics: [] },

          character_custom: (scene?.character_custom || []).map(cc => ({
            ...cc,
            is_dead: cc.is_dead || testGameState.deadCustomCharIds.includes(String(cc.character_id))
          })),
          backgrounds_custom: scene?.backgrounds_custom || [],

          characters_generated: testGameState.generatedCharacters,
          backgrounds_generated: testGameState.generatedBackgrounds,

          current_chars_in_scene: testGameState.currentCharsInScene,
          current_background_id: testGameState.currentBackgroundId,

          music: scene?.music || null,

          additional_prompt_descriptions: scene?.additional_prompt_descriptions || [],
          modifying_states: scene?.modifying_states || [],
          endings: scene?.endings || [],

          current_history: newMessageHistory.map(msg => ({
            id: msg.id, name: msg.name, message: msg.message,
            location_id: msg.location_id || msg.displayInfo?.currentBackgroundId || null,
            characters_in_scene: msg.characters_in_scene || (msg.displayInfo?.currentCharsInScene || []).map(cs => ({
              char_id: cs.char_id, sprite_id: cs.sprite_id || ''
            }))
          }))
        };

        response = await apiPost(API_URLS.TEST_OPEN_WORLD_INTERACTION, openWorldRequest);
      } else {
        // Regular interactive scene request
        const testSceneRequest = {
          user_message: testGameState.userMessage,
          user_biography_name: testGameState.userCharacterName || 'Incognito',
          user_biography_description: testGameState.userCharacterBiography || 'A user testing the interactive scene.',

          music_mixes: novel?.music_mixes || [],

          current_flags: testGameState.flags.map(flag => ({
            id: flag.id, name: flag.name, initial_value: flag.value
          })),
          current_scales: testGameState.scales.map(scale => ({
            id: scale.id, name: scale.name, initial_value: Number.isFinite(scale.value) ? scale.value : 0
          })),
          completed_endings_ids: testGameState.completedEndingIds,

          world_prompts: novel?.world_prompts || [],
          characters: novel?.characters || [],
          assets: novel?.assets || { sprites: [], backgrounds: [], musics: [] },

          scene_characters: scene?.characters || [],
          scene_backgrounds: scene?.backgrounds || [],
          current_scene_sprites: currentSprites.map(s => ({
            character_id: s.character_id,
            character_sprite_id: s.id
          })),
          music: scene?.music || null,

          additional_prompt_descriptions: scene?.additional_prompt_descriptions || [],
          modifying_states: scene?.modifying_states || [],
          endings: scene?.endings || [],
          context_ending: scene?.context_ending || null,

          summaries: [],
          current_history: newMessageHistory.map(msg => ({
            id: msg.id, name: msg.name, message: msg.message
          }))
        };

        response = await apiPost(API_URLS.TEST_INTERACTION, testSceneRequest);
      }

      const gameStateDisplay = response.data;

      // Create user message object (backend handles this in dialogue_history but we need it for UI)
      const userMessage = {
        id: 'USER',
        name: testGameState.userCharacterName || 'Вы',
        message: testGameState.userMessage,
        location_id: testGameState.currentBackgroundId || null,
        characters_in_scene: (testGameState.currentCharsInScene || []).map(cs => ({
          char_id: cs.char_id, sprite_id: cs.sprite_id || ''
        })),
        displayInfo: {
          sprites: getActiveCharacters(),
          background: getActiveBackground(),
          music: getActiveMusic(),
          flags: [...testGameState.flags],
          scales: [...testGameState.scales],
          // Snapshot open world state for rollback
          generatedCharacters: [...testGameState.generatedCharacters],
          generatedBackgrounds: [...testGameState.generatedBackgrounds],
          currentCharsInScene: [...testGameState.currentCharsInScene],
          currentBackgroundId: testGameState.currentBackgroundId,
          deadCustomCharIds: [...testGameState.deadCustomCharIds]
        }
      };

      const newRemoveIds = gameStateDisplay.chars_to_remove_ids || [];
      // Create character message object
      const characterMessage = {
        id: gameStateDisplay.character_id || gameStateDisplay.character_name || 'Character',
        name: gameStateDisplay.character_name || 'Character',
        message: gameStateDisplay.character_message || '',
        location_id: gameStateDisplay.current_background_id || null,
        characters_in_scene: (gameStateDisplay.chars_in_scene || []).map(cs => ({
          char_id: cs.char_id, sprite_id: cs.sprite_id || ''
        })),
        displayInfo: {
          sprites: gameStateDisplay.sprites || [],
          background: gameStateDisplay.background,
          music: gameStateDisplay.music,
          flags: gameStateDisplay.flags || testGameState.flags,
          scales: gameStateDisplay.scales || testGameState.scales,
          // Snapshot open world state for rollback
          generatedCharacters: sceneType === 'open_world' ? [
            ...testGameState.generatedCharacters,
            ...(gameStateDisplay.new_characters || [])
          ] : [],
          generatedBackgrounds: sceneType === 'open_world' ? [
            ...testGameState.generatedBackgrounds,
            ...(gameStateDisplay.new_backgrounds || [])
          ] : [],
          currentCharsInScene: (gameStateDisplay.chars_in_scene || []).filter(
            cs => !newRemoveIds.includes(cs.char_id)
          ),
          currentBackgroundId: gameStateDisplay.current_background_id || null,
          deadCustomCharIds: [...testGameState.deadCustomCharIds, ...newRemoveIds]
        }
      };

      // Build updated message history: previous + user message + character response
      let updatedMessageHistory = [...newMessageHistory, userMessage];
      if (gameStateDisplay.character_message) {
        updatedMessageHistory.push(characterMessage);
      }

      // Check for ending - show the banner whenever an ending_id is returned,
      // resolving a display name with fallbacks so an unnamed/automatic ending still shows.
      let endingName = null;
      if (gameStateDisplay.ending_id) {
        const ending = allEndings.find(e => e.id === gameStateDisplay.ending_id);
        const sceneEnding = (scene?.endings || []).find(e => String(e.id) === String(gameStateDisplay.ending_id));
        endingName = ending?.name || sceneEnding?.name || sceneEnding?.display_name || 'Концовка достигнута';
      }

      // Check if context ending was reached
      const contextEndingReached = gameStateDisplay.context_ending_reached || false;

      // Resolve the next scene defined on the reached ending (regular or context)
      let nextScene = null;
      if (contextEndingReached) {
        nextScene = scene?.context_ending?.next_scene || null;
      } else if (gameStateDisplay.ending_id) {
        const reachedEnding = (scene?.endings || []).find(e => String(e.id) === String(gameStateDisplay.ending_id));
        nextScene = reachedEnding?.next_scene || null;
      }

      // Update the test game state with the response
      setTestGameState(prev => {
        const currentMusicId = prev.currentMusic?.id;
        const newMusicId = gameStateDisplay.music?.length > 0 ? gameStateDisplay.music[0]?.id : gameStateDisplay.music?.id;
        const musicHasChanged = currentMusicId !== newMusicId;

        // Open world: merge generated characters/backgrounds, apply removals
        let updatedGeneratedChars = prev.generatedCharacters;
        let updatedGeneratedBgs = prev.generatedBackgrounds;
        const removeIds = sceneType === 'open_world' ? (gameStateDisplay.chars_to_remove_ids || []) : [];
        if (sceneType === 'open_world') {
          updatedGeneratedChars = [
            ...prev.generatedCharacters,
            ...(gameStateDisplay.new_characters || [])
          ];
          updatedGeneratedBgs = [
            ...prev.generatedBackgrounds,
            ...(gameStateDisplay.new_backgrounds || [])
          ];
          // Mark removed generated characters as dead
          if (removeIds.length > 0) {
            updatedGeneratedChars = updatedGeneratedChars.map(c =>
              removeIds.includes(c.char_id) ? { ...c, is_dead: true } : c
            );
          }
        }

        return {
          ...prev,
          messageHistory: updatedMessageHistory,
          userMessage: '',
          characterMessage: gameStateDisplay.character_message || '',
          characterName: gameStateDisplay.character_name || '',
          flags: gameStateDisplay.flags ? prev.flags.map(flag => {
            const updatedFlag = gameStateDisplay.flags.find(f => f.id === flag.id);
            return updatedFlag ? { ...flag, value: updatedFlag.initial_value } : flag;
          }) : prev.flags,
          scales: gameStateDisplay.scales ? prev.scales.map(scale => {
            const updatedScale = gameStateDisplay.scales.find(s => s.id === scale.id);
            return updatedScale ? { ...scale, value: updatedScale.initial_value } : scale;
          }) : prev.scales,
          completedEndingIds: (() => {
            const base = gameStateDisplay.completed_ending_ids || prev.completedEndingIds;
            // Record the reached ending (regular or context) so it carries into the next scene
            if (gameStateDisplay.ending_id && !base.includes(gameStateDisplay.ending_id)) {
              return [...base, gameStateDisplay.ending_id];
            }
            return base;
          })(),
          currentSprites: gameStateDisplay.sprites || [],
          currentBackground: gameStateDisplay.background || null,
          currentMusic: musicHasChanged ? (Array.isArray(gameStateDisplay.music) ? gameStateDisplay.music[0] : gameStateDisplay.music) || null : prev.currentMusic,
          useDirectAssets: true,
          reachedEndingName: endingName,
          endingReasoning: gameStateDisplay.ending_reasoning || gameStateDisplay.ending_analysis || null,
          contextEndingReached: contextEndingReached,
          nextScene: nextScene,
          // Open world state
          generatedCharacters: updatedGeneratedChars,
          generatedBackgrounds: updatedGeneratedBgs,
          currentCharsInScene: (gameStateDisplay.chars_in_scene || prev.currentCharsInScene).filter(
            cs => !removeIds.includes(cs.char_id)
          ),
          currentBackgroundId: gameStateDisplay.current_background_id || prev.currentBackgroundId,
          deadCustomCharIds: [...prev.deadCustomCharIds, ...removeIds],
        };
      });

      // Update current history index to latest
      setCurrentHistoryIndex(updatedMessageHistory.length - 1);

      // Persist the post-turn flags/scales/completed endings immediately
      const savedFlags = gameStateDisplay.flags
        ? testGameState.flags.map(flag => {
            const uf = gameStateDisplay.flags.find(f => f.id === flag.id);
            return uf ? { ...flag, value: uf.initial_value } : flag;
          })
        : testGameState.flags;
      const savedScales = gameStateDisplay.scales
        ? testGameState.scales.map(scale => {
            const us = gameStateDisplay.scales.find(s => s.id === scale.id);
            return us ? { ...scale, value: us.initial_value } : scale;
          })
        : testGameState.scales;
      const baseCompleted = gameStateDisplay.completed_ending_ids || testGameState.completedEndingIds;
      const savedCompleted = (gameStateDisplay.ending_id && !baseCompleted.includes(gameStateDisplay.ending_id))
        ? [...baseCompleted, gameStateDisplay.ending_id]
        : baseCompleted;
      saveTestState(novelId, {
        flags: savedFlags,
        scales: savedScales,
        completedEndingIds: savedCompleted,
        userCharacterName: testGameState.userCharacterName,
        userCharacterBiography: testGameState.userCharacterBiography
      });

      // Update audio if music has changed
      const musicData = Array.isArray(gameStateDisplay.music) ? gameStateDisplay.music[0] : gameStateDisplay.music;
      if (audioRef && musicData) {
        const newMusicUrl = `${GCS_BUCKET_URL}/${isProduction ? 'production' : 'building'}/${novelId}/music/${musicData.id}.${musicData.extension}${musicData.cache_id ? `?cache_id=${musicData.cache_id}` : ''}`;
        if (audioRef.src !== newMusicUrl) {
          audioRef.src = newMusicUrl;
          if (musicPlaying) {
            audioRef.play().catch(console.error);
          }
        }
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      setError('Failed to send test message');
    } finally {
      setTestLoading(false);
    }
  };

  // Navigate to the next scene defined on the reached ending, opening its test/preview tab
  const goToNextScene = (nextScene) => {
    if (!nextScene?.scene_id) return;
    const sceneTypeUrl = nextScene.scene_type === 'interactive' ? 'interactive_scene'
      : nextScene.scene_type === 'open_world' ? 'open_world_scene' : 'static_scene';
    const tab = nextScene.scene_type === 'static' ? 'preview' : 'testCanvas';
    const basePath = isProduction ? 'production_change' : 'building';
    navigate(`/${basePath}/${novelId}/${sceneTypeUrl}/${nextScene.scene_id}?tab=${tab}`);
  };

  // Handle music toggle
  const toggleMusic = () => {
    if (audioRef) {
      if (musicPlaying) {
        audioRef.pause();
      } else {
        audioRef.play();
      }
      setMusicPlaying(!musicPlaying);
    }
  };

  // Handle static ending choice - applies the ending's deterministic single state/flag changes
  const handleStaticEndingChoice = async (endingId) => {
    if (testLoading) return;

    const staticEnding = (scene?.static_endings || []).find(e => String(e.id) === String(endingId));
    if (!staticEnding) return;

    // Snapshot current states before the choice (covers choices made with no dialogue messages)
    if (!convStartSnapshot) {
      setConvStartSnapshot({
        flags: testGameState.flags.map(f => ({ ...f })),
        scales: testGameState.scales.map(s => ({ ...s })),
        completedEndingIds: [...testGameState.completedEndingIds]
      });
    }

    setTestLoading(true);
    setError('');

    try {
      const response = await apiPost(API_URLS.TEST_STATIC_ENDING, {
        current_flags: testGameState.flags.map(flag => ({
          id: flag.id, name: flag.name, initial_value: flag.value
        })),
        current_scales: testGameState.scales.map(scale => ({
          id: scale.id, name: scale.name, initial_value: scale.value
        })),
        completed_endings_ids: testGameState.completedEndingIds,
        static_ending: staticEnding,
        modifying_states: scene?.modifying_states || [],
        current_history: testGameState.messageHistory.map(msg => ({
          id: msg.id, name: msg.name, message: msg.message
        }))
      });

      const result = response.data;

      // Resolve ending name for the banner
      let endingName = staticEnding.display_name || staticEnding.name;
      const matched = allEndings.find(e => e.id === result.ending_id);
      if (matched) endingName = matched.name;

      // Choice message to mark the selection in history
      const choiceMessage = {
        id: 'USER',
        name: testGameState.userCharacterName || 'Вы',
        message: `[Выбор: ${staticEnding.display_name || staticEnding.name}]`,
        displayInfo: {
          sprites: getActiveCharacters(),
          background: getActiveBackground(),
          music: getActiveMusic(),
          flags: [...testGameState.flags],
          scales: [...testGameState.scales]
        }
      };

      setTestGameState(prev => ({
        ...prev,
        messageHistory: [...prev.messageHistory, choiceMessage],
        userMessage: '',
        flags: result.flags ? prev.flags.map(flag => {
          const updatedFlag = result.flags.find(f => f.id === flag.id);
          return updatedFlag ? { ...flag, value: updatedFlag.initial_value } : flag;
        }) : prev.flags,
        scales: result.scales ? prev.scales.map(scale => {
          const updatedScale = result.scales.find(s => s.id === scale.id);
          return updatedScale ? { ...scale, value: updatedScale.initial_value } : scale;
        }) : prev.scales,
        completedEndingIds: result.completed_ending_ids || prev.completedEndingIds,
        reachedEndingName: endingName,
        endingReasoning: null,
        contextEndingReached: false,
        nextScene: staticEnding.next_scene || null
      }));

      // Persist the post-ending flags/scales/completed endings immediately
      const savedFlags = result.flags
        ? testGameState.flags.map(flag => {
            const uf = result.flags.find(f => f.id === flag.id);
            return uf ? { ...flag, value: uf.initial_value } : flag;
          })
        : testGameState.flags;
      const savedScales = result.scales
        ? testGameState.scales.map(scale => {
            const us = result.scales.find(s => s.id === scale.id);
            return us ? { ...scale, value: us.initial_value } : scale;
          })
        : testGameState.scales;
      saveTestState(novelId, {
        flags: savedFlags,
        scales: savedScales,
        completedEndingIds: result.completed_ending_ids || testGameState.completedEndingIds,
        userCharacterName: testGameState.userCharacterName,
        userCharacterBiography: testGameState.userCharacterBiography
      });
    } catch (error) {
      console.error('Error choosing static ending:', error);
      setError('Failed to apply static ending');
    } finally {
      setTestLoading(false);
    }
  };

  // Handle audio ref callback
  const handleAudioRef = (audio) => {
    setAudioRef(audio);
    if (audio && musicPlaying) {
      audio.play().catch(console.error);
    }
  };

  // Handle history rollback
  const handleHistoryRollback = (index) => {
    if (index < 0 || index >= testGameState.messageHistory.length) return;

    const message = testGameState.messageHistory[index];

    // Can only rollback to character messages
    if (message.id === 'USER') return;

    // Rollback message history to this point
    const newMessageHistory = testGameState.messageHistory.slice(0, index + 1);

    // Restore display info from this message
    const displayInfo = message.displayInfo;

    // Ensure flags and scales have the correct structure with value property
    const restoredFlags = displayInfo?.flags ? displayInfo.flags.map(flag => ({
      id: flag.id,
      name: flag.name,
      value: flag.initial_value !== undefined ? flag.initial_value : flag.value
    })) : [];

    const restoredScales = displayInfo?.scales ? displayInfo.scales.map(scale => ({
      id: scale.id,
      name: scale.name,
      value: scale.initial_value !== undefined ? scale.initial_value : scale.value
    })) : [];

    setTestGameState(prev => ({
      ...prev,
      messageHistory: newMessageHistory,
      characterMessage: message.message,
      characterName: message.name,
      flags: restoredFlags.length > 0 ? restoredFlags : prev.flags,
      scales: restoredScales.length > 0 ? restoredScales : prev.scales,
      userMessage: '',
      // Use direct assets from the message history
      currentSprites: displayInfo?.sprites || [],
      currentBackground: displayInfo?.background || null,
      currentMusic: displayInfo?.music || null,
      // Set useDirectAssets to true to use these assets directly
      useDirectAssets: true,
      reachedEndingName: null,
      endingReasoning: null,
      contextEndingReached: false,
      nextScene: null,
      // Restore open world state from snapshot
      generatedCharacters: displayInfo?.generatedCharacters || prev.generatedCharacters,
      generatedBackgrounds: displayInfo?.generatedBackgrounds || prev.generatedBackgrounds,
      currentCharsInScene: displayInfo?.currentCharsInScene || prev.currentCharsInScene,
      currentBackgroundId: displayInfo?.currentBackgroundId || prev.currentBackgroundId,
      deadCustomCharIds: displayInfo?.deadCustomCharIds || prev.deadCustomCharIds
    }));

    setCurrentHistoryIndex(index);
    setHistoryModalOpen(false);

    // Update audio if music has changed
    if (audioRef && displayInfo?.music) {
      const newMusicUrl = `${GCS_BUCKET_URL}/${isProduction ? 'production' : 'building'}/${novelId}/music/${displayInfo.music.id}.${displayInfo.music.extension}${displayInfo.music.cache_id ? `?cache_id=${displayInfo.music.cache_id}` : ''}`;
      if (audioRef.src !== newMusicUrl) {
        audioRef.src = newMusicUrl;
        if (musicPlaying) {
          audioRef.play().catch(console.error);
        }
      }
    }
  };

  // Open history modal
  const openHistoryModal = () => {
    setHistoryModalOpen(true);
    setTimeout(() => {
      if (historyBodyRef.current) {
        historyBodyRef.current.scrollTop = historyBodyRef.current.scrollHeight;
      }
    }, 0);
  };

  // Close history modal
  const closeHistoryModal = () => {
    setHistoryModalOpen(false);
  };

  return (
    <div className="istc-wrap">
      <div className="istc-header">
        <h3 className="istc-title">Тестовый холст</h3>
        <p className="istc-help">Симулируйте разговоры и наблюдайте, как сцена реагирует на ввод пользователя</p>
      </div>

      <div className="istc-canvas">
        <div className="istc-controls">
          <div className="istc-controls-head">
            <h3 className="istc-controls-title">Элементы управления тестом</h3>
            <button
              className="istc-toggle-btn"
              onClick={() => setTestControlsExpanded(!testControlsExpanded)}
            >
              {testControlsExpanded ? '▾ Скрыть' : '▸ Открыть'}
            </button>
          </div>

          {testControlsExpanded && (
            <div className="istc-controls-body">
              {/* Flags Controls */}
              {testGameState.flags.length > 0 && (
                <div className="istc-section">
                  <h4>Флаги</h4>
                  <div className="istc-flags">
                    {testGameState.flags.map((flag) => (
                      <div key={flag.id} className="istc-flag-item">
                        <label>{flag.name}:
                          <input
                            type="checkbox"
                            checked={flag.value}
                            onChange={(e) => updateFlagValue(flag.id, e.target.checked)}
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scales Controls */}
              {testGameState.scales.length > 0 && (
                <div className="istc-section">
                  <h4>Шкалы</h4>
                  <div className="istc-scales">
                    {testGameState.scales.map((scale) => (
                      <div key={scale.id} className="istc-scale-item">
                        <label>{scale.name}:
                          <input
                            type="number"
                            value={scale.value}
                            onChange={(e) => updateScaleValue(scale.id, e.target.value)}
                            className="istc-scale-input"
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Character Setup */}
              <div className="istc-section">
                <h4>Настройка персонажа</h4>
                <div className="istc-char-setup">
                  <div className="istc-form-group">
                    <label>Имя персонажа:</label>
                    <input
                      type="text"
                      value={testGameState.userCharacterName}
                      onChange={(e) => { const v = e.target.value; setTestGameState(prev => ({...prev, userCharacterName: v})); persistState({ userCharacterName: v }); }}
                      placeholder="Введите имя вашего персонажа..."
                    />
                  </div>
                  <div className="istc-form-group">
                    <label>Биография персонажа:</label>
                    <textarea
                      value={testGameState.userCharacterBiography}
                      onChange={(e) => { const v = e.target.value; setTestGameState(prev => ({...prev, userCharacterBiography: v})); persistState({ userCharacterBiography: v }); }}
                      placeholder="Введите биографию вашего персонажа..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Completed Endings Controls */}
              <div className="istc-section">
                <h4>Завершенные концовки</h4>
                <div className="istc-endings-section">
                  {allEndings && allEndings.length > 0 ? (
                    <>
                      {testGameState.completedEndingIds.length > 0 && (
                        <div className="istc-completed-tags">
                          {testGameState.completedEndingIds.map(id => {
                            const ending = allEndings.find(e => e.id === id);
                            return ending ? (
                              <div key={id} className="istc-completed-tag">
                                <span>{ending.name}</span>
                                <button
                                  className="istc-completed-tag-remove"
                                  onClick={() => {
                                    const next = testGameState.completedEndingIds.filter(eid => eid !== id);
                                    setTestGameState(prev => ({ ...prev, completedEndingIds: next }));
                                    persistState({ completedEndingIds: next });
                                  }}
                                >×</button>
                              </div>
                            ) : null;
                          })}
                        </div>
                      )}
                      <input
                        type="text"
                        className="istc-endings-search"
                        placeholder="Поиск концовки для добавления..."
                        value={endingsSearch}
                        onChange={(e) => setEndingsSearch(e.target.value)}
                      />
                      {endingsSearch.trim() && (() => {
                        const opts = allEndings.filter(e =>
                          e.name.toLowerCase().includes(endingsSearch.toLowerCase()) &&
                          !testGameState.completedEndingIds.includes(e.id)
                        );
                        return (
                          <div className="istc-endings-dropdown">
                            {opts.length > 0 ? opts.map(ending => (
                              <div
                                key={ending.id}
                                className="istc-endings-option"
                                onClick={() => {
                                  const next = [...testGameState.completedEndingIds, ending.id];
                                  setTestGameState(prev => ({ ...prev, completedEndingIds: next }));
                                  persistState({ completedEndingIds: next });
                                  setEndingsSearch('');
                                }}
                              >
                                {ending.name}
                              </div>
                            )) : (
                              <div className="istc-endings-no-results">Концовок не найдено</div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="no-endings-message">
                      В этом романе не определены концовки
                    </div>
                  )}
                </div>
              </div>

              {/* Reset Buttons */}
              <div className="istc-controls-actions">
                <button
                  className="istc-reset-btn"
                  onClick={handleClearHistory}
                  title="Очистить диалог и вернуть флаги, шкалы и концовки к состоянию до первого сообщения"
                >
                  Очистить историю и откатить состояние
                </button>
                <span className={`istc-snapshot-status${convStartSnapshot ? ' istc-snapshot-status--saved' : ''}`}>
                  {convStartSnapshot ? '● Снимок состояния сохранён' : '○ Снимок состояния не сохранён'}
                </span>
                <button
                  className="istc-reset-btn"
                  onClick={() => {
                    if (window.confirm('Сбросить все флаги, шкалы и завершённые концовки к начальным значениям?')) {
                      clearTestState(novelId);
                      handleClearState();
                    }
                  }}
                >
                  Сбросить флаги/шкалы/концовки
                </button>
              </div>
            </div>
          )}
        </div>

        {(testGameState.reachedEndingName || testGameState.contextEndingReached) && (
          <div className="istc-ending-banner">
            {testGameState.contextEndingReached ? (
              <h3>Контекстная концовка достигнута</h3>
            ) : (
              <h3>{'Концовка достигнута'}: {testGameState.reachedEndingName}</h3>
            )}
            {testGameState.nextScene?.scene_id && (
              <button
                className="istc-next-scene-btn"
                onClick={() => goToNextScene(testGameState.nextScene)}
              >
                Перейти к следующей сцене →
              </button>
            )}
          </div>
        )}

        {/* Game Canvas */}
        <div className="istc-game-wrap">
          {/* Game Scene Display */}
          <div className="istc-scene">
            {/* Background */}
            <div className={`istc-bg${testLoading ? ' istc-bg--loading' : ''}`}>
              {(() => {
                const activeBackground = getActiveBackground();
                if (!activeBackground) return (
                  <div className="istc-no-bg">
                    <p>Ни один фон не соответствует текущим условиям</p>
                  </div>
                );
                const bgSrc = activeBackground.is_generated && activeBackground.image_base64
                  ? `data:image/png;base64,${activeBackground.image_base64}`
                  : `${GCS_BUCKET_URL}/${isProduction ? 'production' : 'building'}/${novelId}/background/${activeBackground.id}.${activeBackground.extension}${activeBackground.cache_id ? `?cache_id=${activeBackground.cache_id}` : ''}`;
                return (
                  <img
                    src={bgSrc}
                    alt={activeBackground.name || 'Background'}
                    className="istc-bg-img"
                    onError={(e) => {
                      console.error('Background image failed to load:', activeBackground);
                      e.target.style.display = 'none';
                    }}
                  />
                );
              })()}
            </div>

            {/* Merged Message Interface */}
            <div className="istc-msg-ui" ref={msgUiRef}>
              {/* Character Message and Input in one container */}
              <div className="istc-msg-box" ref={messageContainerRef}>
                {/* Toolbar row */}
                <div className="istc-toolbar">
                  <button
                    className="istc-history-btn"
                    onClick={openHistoryModal}
                    title="История"
                    disabled={testGameState.messageHistory.length === 0}
                  >
                    📖
                  </button>
                  <button
                    className="istc-music-btn"
                    onClick={toggleMusic}
                    title={musicPlaying ? 'Пауза' : 'Воспроизвести'}
                  >
                    {musicPlaying ? '🔊' : '🔇'}
                  </button>
                </div>

                {/* Latest Character Message */}
                {testGameState.characterMessage && testGameState.characterMessage.trim() && (
                  <div className="istc-char-msg">
                    <span className="istc-char-name">{testGameState.characterName || 'Character'}:</span><span className="istc-char-text">{testGameState.characterMessage}</span>
                  </div>
                )}

                {/* Static Ending Choices */}
                {(() => {
                  const availableStaticEndings = getAvailableStaticEndings();
                  return availableStaticEndings.length > 0 && !testGameState.reachedEndingName && !testGameState.contextEndingReached ? (
                    <div className="istc-choices">
                      <div className="istc-choices-btns">
                        {availableStaticEndings.map((ending) => (
                          <button
                            key={ending.id}
                            className="istc-choice-btn"
                            onClick={() => handleStaticEndingChoice(ending.id)}
                            disabled={testLoading}
                          >
                            {ending.display_name || ending.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Message Input Row - Only show if scene allows user messages */}
                {scene?.interactive !== false ? (
                  <div className="istc-input-row">
                    <input
                      type="text"
                      value={testGameState.userMessage}
                      onChange={(e) => setTestGameState(prev => ({...prev, userMessage: e.target.value}))}
                      placeholder="Введите ваше сообщение..."
                      className="istc-msg-input"
                      onKeyPress={(e) => e.key === 'Enter' && handleSendTestMessage()}
                      disabled={testLoading || testGameState.reachedEndingName || testGameState.contextEndingReached}
                    />
                    <button
                      className="istc-send-btn"
                      onClick={handleSendTestMessage}
                      disabled={!testGameState.userMessage.trim() || testLoading || testGameState.reachedEndingName || testGameState.contextEndingReached}
                      title="Отправить"
                    >
                      ▶
                    </button>
                  </div>
                ) : (
                  <div className="istc-non-interactive">
                    <p>Эта сцена настроена как неинтерактивная. Пользователи могут только выбирать из статических окончаний.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Character Sprites - CONTAINED within canvas */}
            <div className="istc-sprites" style={{ top: spritesTop }}>
              {(() => {
                const activeCharacters = getActiveCharacters();
                const sortedCharacters = [...activeCharacters].sort((a, b) => {
                  if (!a.character_id || !b.character_id) return 0;
                  return a.character_id.localeCompare(b.character_id);
                });

                return sortedCharacters.length > 0 ? (
                  sortedCharacters.map((sprite, index) => {
                    const totalSprites = sortedCharacters.length;

                    // Force sprites to fit within container
                    const spriteWidth = 90 / totalSprites; // Distribute evenly across 90% of container

                    // Center the sprites horizontally
                    const totalWidthUsed = totalSprites * spriteWidth;
                    const startPosition = (100 - totalWidthUsed) / 2; // Center the group
                    const leftPosition = startPosition + (index * spriteWidth);

                    const imgSrc = sprite.is_generated && sprite.sprite_base64
                      ? `data:image/png;base64,${sprite.sprite_base64}`
                      : `${GCS_BUCKET_URL}/${isProduction ? 'production' : 'building'}/${novelId}/sprite/${sprite.id}.${sprite.extension}${sprite.cache_id ? `?cache_id=${sprite.cache_id}` : ''}`;

                    return (
                      <div
                        key={sprite.id}
                        className="istc-sprite"
                        style={{
                          left: `${leftPosition}%`,
                          width: `${spriteWidth}%`,
                          zIndex: 10 + totalSprites - index
                        }}
                      >
                        <img
                          src={imgSrc}
                          alt={sprite.name || 'Спрайт персонажа'}
                          className="istc-sprite-img"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    );
                  })
                ) : (
                  <div className="istc-no-chars">
                    <p>Ни один персонаж не соответствует текущим условиям</p>
                  </div>
                );
              })()}
            </div>

            {/* Hidden Audio Player for Music */}
            {(() => {
              const activeMusic = getActiveMusic();
              return activeMusic ? (
                <audio
                  ref={handleAudioRef}
                  autoPlay={musicPlaying}
                  loop
                  style={{ display: 'none' }}
                >
                  <source
                    src={`${GCS_BUCKET_URL}/${isProduction ? 'production' : 'building'}/${novelId}/music/${activeMusic.id}.${activeMusic.extension}${activeMusic.cache_id ? `?cache_id=${activeMusic.cache_id}` : ''}`}
                    type={`audio/${activeMusic.extension}`}
                  />
                </audio>
              ) : null;
            })()}
          </div>

          {/* History Modal - Now inside the game background */}
          {historyModalOpen && (
            <>
            <div className="istc-history-backdrop" onClick={closeHistoryModal} />
            <div
              className="istc-history-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="istc-history-head">
                <h3 className="istc-history-title">История сообщений</h3>
                <button
                  onClick={closeHistoryModal}
                  className="istc-history-close"
                >
                  ×
                </button>
              </div>
              <div className="istc-history-body" ref={historyBodyRef}>
                {testGameState.messageHistory.length > 0 ? (
                  <div className="istc-history-list">
                    {testGameState.messageHistory.map((msg, index) => (
                      <div
                        key={index}
                        className={`istc-history-msg ${msg.id === 'USER' ? 'istc-history-msg--user' : 'istc-history-msg--char'}`}
                        onClick={() => handleHistoryRollback(index)}
                        title={msg.id !== 'USER' ? 'Нажмите, чтобы откатиться к этому сообщению' : ''}
                      >
                        <span className="istc-history-msg-name">
                          {msg.id === 'USER' ? 'Вы' : msg.name}
                          {index === currentHistoryIndex && (
                            <span className="istc-history-msg-current">
                              Текущее
                            </span>
                          )}
                        </span>
                        <div>{msg.message}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="istc-history-empty">
                    История пуста
                  </div>
                )}
              </div>
            </div>
            </>
          )}
        </div>
      </div>

      {testGameState.endingReasoning && (
        <div className="istc-reasoning">
          <h4>Обоснование</h4>
          <p>{testGameState.endingReasoning}</p>
        </div>
      )}

    </div>
  );
};

export default InteractiveSceneTestCanvas;
